import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, makeStyles } from '@material-ui/core'
import { DAYCARE, DAYPASS, GROOMING } from '../../../utils'
import { PROMO_SERVICE_TARGETS } from '../../../utils'

import CreditsTable from '../../Tables/CreditsTable'
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme) => ({
	card: {
		borderRadius: 6,
		backgroundColor: '#F3F3F3'
	}
}))

const ServiceBankCard = (props) => {
	const classes = useStyles()
	const facilities = useSelector((state) => state.specific.facilities.facilities)
	const household = useSelector((state) => state.specific.households.selected)

	const { market_id: marketId, default_facility_id } = household || {}

	const initialCredits = [
		{
			service: '20 Minute',
			type: 20,
			marketId,
			availableCredits: 0,
			autoRefillAmount: 0,
			autoRefill: {
				enabled: false
			},
			serviceTarget: PROMO_SERVICE_TARGETS.HOME_SERVICES_CREDITS_ONLY
		},
		{
			service: '40 Minute',
			type: 40,
			marketId,
			availableCredits: 0,
			autoRefillAmount: 0,
			autoRefill: {
				enabled: false
			},
			serviceTarget: PROMO_SERVICE_TARGETS.HOME_SERVICES_CREDITS_ONLY
		},
		{
			service: '60 Minute',
			type: 60,
			marketId,
			availableCredits: 0,
			autoRefillAmount: 0,
			autoRefill: {
				enabled: false
			},
			serviceTarget: PROMO_SERVICE_TARGETS.HOME_SERVICES_CREDITS_ONLY
		},
		{
			service: 'Playcare',
			type: 11,
			marketId,
			availableCredits: 0,
			autoRefillAmount: 0,
			autoRefill: {
				enabled: false
			},
			serviceTarget: null
		},
		{
			service: 'Grooming',
			type: GROOMING,
			marketId,
			availableCredits: 0,
			autoRefillAmount: '--',
			autoRefill: {
				enabled: 'n/a'
			},
			serviceTarget: PROMO_SERVICE_TARGETS.GROOMING
		},
		{
			service: 'Enrichment Bank',
			type: 'addon',
			availableCredits: 0,
			autoRefillAmount: '--',
			autoRefill: {
				enabled: 'n/a'
			},
			serviceTarget: null
		}
	]

	const [credits, setCredits] = useState(initialCredits)

	useEffect(() => {
		if (household && facilities) {
			updateCredits()
		}
	}, [household, facilities])

	const updateCredits = () => {
		let initialCreds = [...initialCredits]
		if (household.credits) {
			let creds = household.credits

			const { daycare: householdDaycare } = creds
			const { daypass: householdDaypasses } = creds
			if (householdDaypasses) {
				const daypass_aggregate = {
					availableCredits: householdDaypasses.length,
					facilityId: default_facility_id,
					marketId,
					type: DAYPASS,
					service: 'Park Daypass',
					servictTarget: PROMO_SERVICE_TARGETS.BAR_PARK,
					autoRefillAmount: '--',
					autoRefill: {
						enabled: 'n/a'
					}
				}
				initialCreds.push(daypass_aggregate)
			}
			if (householdDaycare) {
				const daycare_aggregate = {
					availableCredits: 0,
					autoRefillAmount: household?.auto_renew?.daycare || 0,
					facilityId: default_facility_id,
					marketId,
					service: 'Daycare',
					servictTarget: PROMO_SERVICE_TARGETS.DAYCARE_CREDITS_ONLY,
					type: DAYCARE,
					autoRefill: {
						enabled: household?.auto_renew?.daycare > 0
					},
					facilityBreakdown: []
				}
				const remainingByFacility = {}
				const facilityNameById = {}
				for (const facility of facilities) {
					if (facility?._id) {
						facilityNameById[String(facility._id)] = facility.name
					}
				}
				for (const facilityDaycare of householdDaycare) {
					if (
						!facilityDaycare.used &&
						!facilityDaycare.expired &&
						facilityDaycare.hasOwnProperty('credit_remaining') &&
						facilityDaycare.credit_remaining > 0
					) {
						daycare_aggregate.availableCredits += facilityDaycare.credit_remaining
						const facilityId = facilityDaycare.facility_id
							? String(facilityDaycare.facility_id)
							: 'unassigned'
						remainingByFacility[facilityId] =
							(remainingByFacility[facilityId] || 0) + facilityDaycare.credit_remaining
					}
				}
				daycare_aggregate.facilityBreakdown = Object.entries(remainingByFacility)
					.map(([facilityId, credits]) => ({
						facilityId,
						name:
							facilityId === 'unassigned'
								? 'Unassigned'
								: facilityNameById[facilityId] || 'Unknown facility',
						credits
					}))
					.sort((a, b) => b.credits - a.credits || a.name.localeCompare(b.name))
				initialCreds.unshift(daycare_aggregate)
			}

			for (const cred of Object.keys(creds)) {
				if (cred === DAYPASS) {
					continue
				}
				for (const credit of creds[cred]) {
					if (credit.type === DAYCARE) {
						// daycare is handled above as an aggregation now
						continue
					}
					const typeIndex = initialCreds.findIndex((initialCred) => {
						return initialCred.type === credit.type
					})
					if (!credit.used && !credit.expired && !credit.visit_used_on) {
						initialCreds[typeIndex].availableCredits++
					}
				}
			}

			if (household.addon_bank) {
				let cred = initialCreds.find((c) => c.type === 'addon')
				if (cred) cred.availableCredits = `$${(household.addon_bank / 100).toFixed(2)}`
			}
		}

		if (household.auto_renew) {
			Object.keys(household.auto_renew).forEach((key) => {
				if (key === DAYCARE) {
					return
				}
				key = key !== DAYCARE ? parseInt(key) : key

				const initalCredIndexesFilteredByKey = initialCreds
					.map((c, i) => (c.type === key ? i : -1))
					.filter((i) => i !== -1)

				for (const indx of initalCredIndexesFilteredByKey) {
					initialCreds[indx].autoRefillAmount = household.auto_renew[key] || 0
					if (household.auto_renew[key]) {
						initialCreds[indx].autoRefill.enabled = true
					}
				}
			})
		}

		setCredits(initialCreds)
	}

	return (
		<Card className={classes.card}>
			<CardHeader title="Service Bank" />
			<CardContent>
				<CreditsTable data={credits} setCredits={setCredits} />
			</CardContent>
		</Card>
	)
}

export default ServiceBankCard
